import { useEffect } from "react";

import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { getOrganizationId } from "Libs/utils";
import { RootState } from "Store/configureStore";
import { useAppDispatch } from "Store/hooks";

import {
  selectCarbonEmissionsStateByOrganizationId,
  selectCarbonEmissionsMonthlyDataForChart,
  selectCarbonEmissionsPreviousYearTotal,
  selectCarbonEmissionsProjectsTableData
} from "../carbonEmissions.selectors";
import { ensureOrganizationCarbonEmissionsLoaded } from "../thunks/ensureOrganizationCarbonEmissionsLoaded.thunk";

export const useOrganizationCarbonEmissions = (organizationName?: string) => {
  const dispatch = useAppDispatch();
  const { organizationId: urlOrganizationName } = useParams<{
    organizationId: string;
  }>();
  const effectiveOrgName = organizationName || urlOrganizationName;

  const organizationId = useSelector((state: RootState) =>
    getOrganizationId(state, effectiveOrgName)
  );

  const state = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsStateByOrganizationId(state, { organizationId })
      : {
          data: null,
          isLoading: false,
          error: null,
          isNonRetryableError: false
        }
  );

  const monthlyChartData = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsMonthlyDataForChart(state, { organizationId })
      : []
  );

  const previousYearTotal = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsPreviousYearTotal(state, { organizationId })
      : 0
  );

  const projectsTableData = useSelector((state: RootState) =>
    organizationId
      ? selectCarbonEmissionsProjectsTableData(state, { organizationId })
      : []
  );

  // Simple effect - let the thunk handle all the loading logic
  useEffect(() => {
    if (organizationId) {
      dispatch(ensureOrganizationCarbonEmissionsLoaded(organizationId));
    }
  }, [dispatch, organizationId]);

  return {
    isLoading: state.isLoading,
    error: state.error,
    monthlyChartData,
    previousYearTotal,
    projectsTableData
  };
};
